<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import { t, translate } from "./lib/i18n";

  // Icons
  import dashIcon from "@iconify/icons-mdi/view-dashboard-outline";
  import sendIcon from "@iconify/icons-mdi/send";
  import imageIcon from "@iconify/icons-mdi/file-image-outline";
  import folderIcon from "@iconify/icons-mdi/folder-outline";
  import tasksIcon from "@iconify/icons-mdi/format-list-checks";
  import idIcon from "@iconify/icons-mdi/identifier";
  import workersIcon from "@iconify/icons-mdi/account-group-outline";
  import robotIcon from "@iconify/icons-mdi/robot-outline";
  import chevronRightIcon from "@iconify/icons-mdi/chevron-right";
  import chevronDownIcon from "@iconify/icons-mdi/chevron-down";
  import closeIcon from "@iconify/icons-mdi/close";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import infoIcon from "@iconify/icons-mdi/information-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import textIcon from "@iconify/icons-mdi/text-long";
  import webhookIcon from "@iconify/icons-mdi/webhook";

  // Provided by App.svelte
  export let apiBase = "";
  export let apiKey = ""; // bearer token from localStorage (or empty)
  export let authMode = false;
  export let paused = false;
  export let connected = false;
  export let setRefresh = (fn) => {};
  let autoSelected = false;

  // Snapshot state
  let snap = null;
  let snapErr = "";

  // Inline details pane
  let selectedWorkerIndex = null;
  $: selected =
    (snap?.workers || []).find((w) => w?.worker_index === selectedWorkerIndex) || null;
  $: availableTasks = tasks.filter((task) => task?.available);

  // Push form
  let inputKey = "sample.png";
  let outputKey = "test-output";
  let documentId = String(randInt(10000, 99999));
  let selectedTask = "";
  let webhook = "";

  let pushMsg = "";
  let pushErr = "";
  let tasks = [];
  let tasksErr = "";

  // Ingest dropdown state
  let ingestOpen = false;
  let ingestLoading = false;
  let ingestErr = "";
  let ingestItems = [];
  let inflightIngest = null;

  // Polling
  let timer = null;
  let inflight = null;

  const JSON_HEADERS = { Accept: "application/json" };

  function randInt(min, max) {
    return Math.floor(min + Math.random() * (max - min + 1));
  }

  function randStr(len = 8) {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    let out = "";
    for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  }

  function slugify(s) {
    return String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\.[a-z0-9]+$/i, "") // drop extension
      .replace(/[^a-z0-9]+/g, "-") // non-alnum -> -
      .replace(/^-+|-+$/g, "") // trim -
      .replace(/-+/g, "-"); // collapse
  }

  function guessOutputFromInput(key) {
    const base = slugify(key);
    const suf = randStr(8);
    return base ? `${base}-${suf}` : `output-${suf}`;
  }

  function safe(v) {
    return v == null ? "" : String(v);
  }

  function clampPct(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  function shortPath(p) {
    const s = safe(p).trim();
    if (!s) return "";
    const parts = s.split("/").filter(Boolean);
    if (parts.length <= 2) return s;
    return parts.slice(-2).join("/");
  }

  function jobLine(w) {
    const inKey = safe(w?.job?.input_key) || safe(w?.current_input_path) || "";
    const outKey = safe(w?.job?.output_key) || safe(w?.current_output_dir) || "";
    if (!inKey && !outKey) return "";
    return `${shortPath(inKey) || "\u2014"} \u2192 ${shortPath(outKey) || "\u2014"}`;
  }

  function isWorking(stage) {
    const s = String(stage || "").toLowerCase();
    return s && s !== "idle" && s !== "finished" && s !== "error";
  }

  function stageTone(stage) {
    const s = String(stage || "").toLowerCase();
    if (s === "finished") return "ok";
    if (s === "error") return "bad";
    if (s === "converting") return "warn";
    if (s === "idle") return "idle";
    return "info";
  }

  function stagePillClass(stage) {
    const t = stageTone(stage);
    const base =
      "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium lowercase";
    if (t === "ok") return `${base} border-emerald-400/30 bg-emerald-400/10 text-emerald-100`;
    if (t === "bad") return `${base} border-rose-400/35 bg-rose-400/10 text-rose-100`;
    if (t === "warn") return `${base} border-orange-300/35 bg-orange-300/10 text-orange-100`;
    if (t === "info") return `${base} border-sky-300/30 bg-sky-300/10 text-sky-100`;
    return `${base} border-white/10 bg-white/[0.04] text-zinc-200`;
  }

  function selectWorker(idx) {
    selectedWorkerIndex = idx;
  }

  function clearSelection() {
    selectedWorkerIndex = null;
  }

  function authHeaderMaybe() {
    const k = String(apiKey || "").trim();
    return k ? { Authorization: `Bearer ${k}` } : {};
  }

  function startPolling() {
    if (timer) return;
    timer = setInterval(fetchSnapshot, 500);
  }

  function stopPolling() {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  }

  async function fetchSnapshot() {
    if (paused) return;

    const k = String(apiKey || "").trim();
    if (authMode && !k) return; // only block when auth is enabled

    try {
      inflight?.abort?.();
    } catch {}
    inflight = new AbortController();

    try {
      const res = await fetch(`${apiBase}/`, {
        method: "GET",
        cache: "no-store",
        signal: inflight.signal,
        headers: {
          ...JSON_HEADERS,
          ...(k ? { Authorization: `Bearer ${k}` } : {}) // only send if present
        }
      });

      if (!res.ok) throw new Error(`snapshot failed: ${res.status}`);
      snap = await res.json();
      snapErr = "";
      connected = true;
    } catch (e) {
      if (e?.name === "AbortError") return;
      connected = false;
      snapErr = e?.message || String(e);
    }
  }

  async function fetchIngest() {
    ingestErr = "";
    ingestLoading = true;

    const k = String(apiKey || "").trim();
    if (authMode && !k) {
      ingestLoading = false;
      ingestErr = translate("dashboard.apiKeyRequired");
      return;
    }

    try {
      inflightIngest?.abort?.();
    } catch {}
    inflightIngest = new AbortController();

    try {
      const res = await fetch(`${apiBase}/ingest`, {
        method: "GET",
        cache: "no-store",
        signal: inflightIngest.signal,
        headers: {
          ...JSON_HEADERS,
          ...(k ? { Authorization: `Bearer ${k}` } : {})
        }
      });

      let body = null;
      try {
        body = await res.json();
      } catch {}

      if (!res.ok) throw new Error(body?.error || `ingest failed: ${res.status}`);

      ingestItems = body?.entries || [];
    } catch (e) {
      if (e?.name === "AbortError") return;
      ingestItems = [];
      ingestErr = e?.message || String(e);
    } finally {
      ingestLoading = false;
    }
  }

  async function fetchTasks() {
    tasksErr = "";

    const k = String(apiKey || "").trim();
    if (authMode && !k) {
      tasksErr = translate("dashboard.apiKeyRequired");
      return;
    }

    try {
      const res = await fetch(`${apiBase}/tasks`, {
        method: "GET",
        cache: "no-store",
        headers: {
          ...JSON_HEADERS,
          ...(k ? { Authorization: `Bearer ${k}` } : {})
        }
      });

      let body = null;
      try {
        body = await res.json();
      } catch {}

      if (!res.ok) throw new Error(body?.error || `tasks failed: ${res.status}`);

      tasks = Array.isArray(body?.tasks) ? body.tasks : [];
      const available = tasks.filter((task) => task?.available);
      if (!available.some((task) => task?.name === selectedTask)) {
        selectedTask = available[0]?.name || "";
      }
    } catch (e) {
      tasks = [];
      selectedTask = "";
      tasksErr = e?.message || String(e);
    }
  }

  async function toggleIngest() {
    ingestOpen = !ingestOpen;
    if (ingestOpen && !ingestItems.length && !ingestLoading) {
      await fetchIngest();
    }
  }

  function pickIngestFile(name) {
    inputKey = String(name || "").trim();
    outputKey = guessOutputFromInput(inputKey);
    ingestOpen = false;
  }

  async function pushJob(e) {
    e.preventDefault();
    pushMsg = "";
    pushErr = "";

    const payload = {
      input_key: String(inputKey || "").trim(),
      output_key: String(outputKey || "").trim(),
      document_id: String(documentId || "").trim(),
      tasks: [String(selectedTask || "").trim()]
    };

    const wh = String(webhook || "").trim();
    if (wh) payload.webhook = wh;

    if (!payload.input_key) return (pushErr = translate("dashboard.inputRequired"));
    if (!payload.output_key) return (pushErr = translate("dashboard.outputRequired"));
    if (!payload.document_id) return (pushErr = translate("dashboard.documentRequired"));
    if (!payload.tasks[0]) return (pushErr = translate("dashboard.taskRequired"));

    try {
      const res = await fetch(`${apiBase}/push`, {
        method: "POST",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...authHeaderMaybe()
        },
        body: JSON.stringify(payload)
      });

      let body = null;
      try {
        body = await res.json();
      } catch {}

      if (!res.ok) throw new Error(body?.error || `push failed: ${res.status}`);

      pushMsg = body?.job_id ? translate("dashboard.queuedJobId", { jobId: body.job_id }) : translate("dashboard.queuedJob");
      documentId = String(randInt(10000, 99999));
      webhook = "";
      fetchSnapshot();
    } catch (err) {
      pushErr = err?.message || String(err);
    }
  }

  onMount(() => {
    setRefresh(fetchSnapshot);
    fetchSnapshot();
    fetchTasks();
    startPolling();
  });

  // Stop polling while paused; resume when unpaused.
  $: if (paused) stopPolling();
  $: if (!paused && (!authMode || String(apiKey || "").trim())) startPolling();

  onDestroy(() => {
    stopPolling();
    try {
      inflight?.abort?.();
    } catch {}
    try {
      inflightIngest?.abort?.();
    } catch {}
  });

  const inputCls =
    "h-10 w-full rounded-xl border border-white/10 bg-black/30 px-3 text-sm " +
    "text-zinc-100 placeholder:text-zinc-500 outline-none " +
    "focus:border-zinc-400/70 focus:ring-2 focus:ring-zinc-400/10";

  const labelCls = "mb-1.5 flex items-center gap-2 text-xs text-zinc-400";

  // Fallback if you haven't added theme classes yet:
  const cardFallback = "rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur";
  const subcardFallback = "rounded-xl border border-white/10 bg-black/20";
  const card = "ui-card " + cardFallback;
  const subcard = subcardFallback;
  const btn = "ui-btn";
  const btnAccent = "ui-btn-accent";
</script>

<main class="w-full px-3 py-4 sm:px-4 sm:py-6">
  <!-- Top card: header + queue -->
  <section class={`${card} p-5 sm:p-6 relative ${ingestOpen ? "z-50" : "z-10"}`}>
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-cyan-400/50 bg-cyan-500/30 text-cyan-50"
        >
          <Icon icon={dashIcon} width="18" />
        </span>

        <div>
          <h1 class="text-lg font-light tracking-wide text-zinc-100">{$t('dashboard.title')}</h1>
          <p class="mt-0.5 text-sm text-zinc-400">{$t('dashboard.subtitle')}</p>
        </div>
      </div>

      <div class="flex items-center gap-2 text-xs text-zinc-400">
        <Icon icon={clockIcon} width="14" />
        <span class="font-medium text-zinc-300">{snap?.meta?.uptime || "\u2014"}</span>
      </div>
    </div>

    <div class="mt-6">
      <form class="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-12" on:submit={pushJob}>
        <!-- INPUT FILE with dropdown -->
        <label class="lg:col-span-3 relative">
          <div class={labelCls}>
            <Icon icon={imageIcon} width="14" />
            <span>{$t('dashboard.inputFile')}</span>
          </div>

          <div class="relative flex">
            <input
              class={`${inputCls} pr-12 rounded-r-none`}
              bind:value={inputKey}
              spellcheck="false"
              required
            />

            <button
              type="button"
              class="h-10 w-12 rounded-xl rounded-l-none border border-l-0 border-white/10 bg-black/30
                     text-zinc-200 hover:bg-white/[0.06] flex items-center justify-center"
              on:click={toggleIngest}
              title={$t('dashboard.pickFromS3')}
              aria-expanded={ingestOpen}
            >
              <Icon icon={chevronDownIcon} width="20" />
            </button>

            {#if ingestOpen}
              <div
                class="absolute z-50 mt-1 top-full left-0 right-0 rounded-xl border border-white/10
                       bg-black/80 backdrop-blur-xl overflow-hidden shadow-lg"
              >
                {#if ingestLoading}
                  <div class="px-3 py-2 text-sm text-zinc-400">{$t('common.loading')}</div>
                {:else if ingestErr}
                  <div class="px-3 py-2 text-sm text-rose-200">{ingestErr}</div>
                {:else if !ingestItems.length}
                  <div class="px-3 py-2 text-sm text-zinc-400">{$t('dashboard.noFilesFound')}</div>
                {:else}
                  <div class="max-h-64 overflow-auto">
                    <div class="divide-y divide-white/10">
                      {#each ingestItems as f (f.key || f.rel_key || f.name)}
                        <button
                          type="button"
                          class="w-full text-left px-3 py-2 hover:bg-white/[0.06] text-sm text-zinc-200"
                          on:click={() => pickIngestFile(f.rel_key || f.name)}
                          title={f.key}
                        >
                          <div class="font-mono truncate">{f.rel_key || f.name}</div>
                          <div class="mt-0.5 text-xs text-zinc-500">
                            {f.last_modified ? new Date(f.last_modified).toLocaleString() : "\u2014"}
                          </div>
                        </button>
                      {/each}
                    </div>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </label>

        <!-- OUTPUT -->
        <label class="lg:col-span-2">
          <div class={labelCls}>
            <Icon icon={folderIcon} width="14" />
            <span>{$t('dashboard.outputFile')}</span>
          </div>
          <input class={inputCls} bind:value={outputKey} spellcheck="false" required />
        </label>

        <!-- TASK -->
        <label class="lg:col-span-3">
          <div class={labelCls}>
            <Icon icon={tasksIcon} width="14" />
            <span>{$t('dashboard.task')}</span>
          </div>
          <select class={inputCls} bind:value={selectedTask} required disabled={tasksErr || !availableTasks.length}>
            {#if tasksErr}
              <option value="">{$t('dashboard.tasksUnavailable')}</option>
            {:else if !availableTasks.length}
              <option value="">{$t('dashboard.noTasksFound')}</option>
            {:else}
              {#each availableTasks as task (task.name)}
                <option value={task.name}>{task.name}</option>
              {/each}
            {/if}
          </select>
        </label>

        <!-- DOCUMENT ID -->
        <label class="lg:col-span-1">
          <div class={labelCls}>
            <Icon icon={idIcon} width="14" />
            <span>{$t('dashboard.documentId')}</span>
          </div>
          <input class={inputCls} bind:value={documentId} spellcheck="false" />
        </label>

        <!-- WEBHOOK -->
        <label class="lg:col-span-2">
          <div class={labelCls}>
            <Icon icon={webhookIcon} width="14" />
            <span>{$t('dashboard.webhookOptional')}</span>
          </div>
          <input
            class={inputCls}
            type="url"
            bind:value={webhook}
            placeholder="https://example.com/webhook"
            spellcheck="false"
          />
        </label>

        <div class="lg:col-span-1 flex items-end">
          <button class={`${btnAccent} w-full`} type="submit" title={$t('dashboard.queueJob')}>
            <Icon icon={sendIcon} width="18" />
            <span class="hidden sm:inline">{$t('dashboard.queue')}</span>
          </button>
        </div>
      </form>

      {#if pushMsg}
        <div
          class="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100"
        >
          {pushMsg}
        </div>
      {/if}

      {#if pushErr}
        <div
          class="mt-3 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
        >
          {pushErr}
        </div>
      {/if}
    </div>
  </section>

  <!-- Main content: workers list + inline details -->
  <section class="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-12">
    <!-- Workers list -->
    <div class="xl:col-span-8">
      <div class={`${card} overflow-hidden`}>
        <div
          class="flex flex-col gap-2 border-b border-white/10 bg-white/[0.02] px-5 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:px-6"
        >
          <div class="flex items-center gap-2">
            <Icon icon={workersIcon} width="18" />
            <div class="text-sm font-semibold text-zinc-100">{$t('dashboard.workers')}</div>
          </div>

          <div class="text-xs text-zinc-400">
            {#if snap?.meta?.workers}
              {$t('dashboard.count')} <span class="text-zinc-200">{snap.meta.workers.count}</span>
              <span class="text-white/10"> &bull; </span>
              {$t('dashboard.idle')} <span class="text-zinc-200">{snap.meta.workers.idle}</span>
              <span class="text-white/10"> &bull; </span>
              {$t('dashboard.busy')} <span class="text-zinc-200">{snap.meta.workers.busy}</span>
              <span class="text-white/10"> &bull; </span>
              {$t('common.error')} <span class="text-zinc-200">{snap.meta.workers.error}</span>
            {:else}
              &mdash;
            {/if}
          </div>
        </div>

        {#if snapErr}
          <div class="px-5 py-4 sm:px-6">
            <div
              class="rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
            >
              <Icon icon={alertIcon} width="18" />
              <span>{snapErr}</span>
            </div>
          </div>
        {/if}

        <div class="hidden md:grid grid-cols-[140px_170px_1fr_120px] gap-3 px-5 py-3 text-xs text-zinc-400 sm:px-6">
          <div>{$t('dashboard.worker')}</div>
          <div>{$t('dashboard.stage')}</div>
          <div>{$t('dashboard.job')}</div>
          <div class="text-right">{$t('dashboard.progress')}</div>
        </div>

        <div class="divide-y divide-white/10">
          {#if (snap?.workers || []).length}
            {#each snap.workers as w (w.worker_index)}
              <button
                type="button"
                class={`w-full text-left px-5 py-3 sm:px-6 transition-colors ${
                  selectedWorkerIndex === w.worker_index
                    ? "relative bg-cyan-500/10 ring-1 ring-cyan-400/60 before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-cyan-400 before:shadow-[0_0_18px_rgba(34,211,238,0.85)]"
                    : "hover:bg-white/[0.03]"
                }`}
                on:click={() => selectWorker(w.worker_index)}
              >
                <div class="grid grid-cols-1 gap-2 md:grid-cols-[140px_170px_1fr_120px] md:items-center md:gap-3">
                  <div class="flex items-center gap-2 font-semibold text-zinc-100">
                    <Icon icon={robotIcon} width="18" />
                    <span>#{w.worker_index}</span>
                  </div>

                  <div>
                    <span class={stagePillClass(w.stage)}>{w.stage}</span>
                  </div>

                  <div class="min-w-0">
                    {#if isWorking(w?.stage)}
                      <div class="flex items-center gap-2 text-sm text-zinc-200">
                        <Icon icon={chevronRightIcon} width="18" class="shrink-0" />
                        <span class="truncate">{jobLine(w) || "\u2014"}</span>
                      </div>

                      {#if clampPct(w?.progress?.percent) > 0}
                      <div class="mt-2 h-[2px] w-full rounded-full bg-white/10 overflow-hidden">
                        <div
                          class="h-full bg-blue-500/40"
                          style={`width: ${clampPct(w?.progress?.percent)}%`}
                        ></div>
                      </div>
                    {/if}
                    {:else}
                      <span class="text-sm text-zinc-500">&mdash;</span>
                    {/if}
                  </div>

                  <div
                  class={`text-right text-sm tabular-nums ${
                    clampPct(w?.progress?.percent) > 0 ? "text-blue-400" : "text-zinc-500"
                  }`}
                >
                  {clampPct(w?.progress?.percent)}%
                </div>
                </div>
                <!-- subtle progress meter -->

              </button>
            {/each}
          {:else}
            <div class="px-5 py-6 text-sm text-zinc-500 sm:px-6">{$t('dashboard.waitingForSnapshot')}</div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Inline details -->
    <div class="xl:col-span-4">
      <div class={`${card} p-5 sm:p-6`}>
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="flex items-center gap-2 text-sm font-semibold text-zinc-100">
              <Icon icon={robotIcon} width="18" />
              <span>{$t('dashboard.workerDetails')}</span>
            </div>
            <div class="mt-1 text-xs text-zinc-400">{$t('dashboard.selectWorker')}</div>
          </div>

          {#if selectedWorkerIndex != null}
            <button class={btn} type="button" on:click={clearSelection} title={$t('dashboard.clearSelection')}>
              <Icon icon={closeIcon} width="18" />
              <span class="sr-only">{$t('dashboard.clear')}</span>
            </button>
          {/if}
        </div>

        {#if selectedWorkerIndex == null}
          <div class="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
            {$t('dashboard.noWorkerSelected')}
          </div>
        {:else if !selected}
          <div class="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
            {$t('dashboard.workerNotFound')}
          </div>
        {:else}
          <div class="mt-5 grid gap-4">
            <div class={`${subcard} p-4`}>
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-2 text-sm font-semibold text-zinc-100">
                  <Icon icon={robotIcon} width="18" />
                  <span>#{selectedWorkerIndex}</span>
                </div>
                <span class={stagePillClass(selected?.stage)}>{safe(selected?.stage) || "\u2014"}</span>
              </div>

              <div class="mt-4 grid gap-3 text-sm">
                <div class="grid grid-cols-[110px_1fr] gap-3">
                  <div class="flex items-center gap-2 text-xs text-zinc-400">
                    <Icon icon={infoIcon} width="14" />
                    <span>{$t('dashboard.message')}</span>
                  </div>
                  <div class="text-zinc-200 break-words">{safe(selected?.message) || "\u2014"}</div>
                </div>

                <div class="grid grid-cols-[110px_1fr] gap-3">
                  <div class="flex items-center gap-2 text-xs text-zinc-400">
                    <Icon icon={idIcon} width="14" />
                    <span>{$t('dashboard.jobId')}</span>
                  </div>
                  <div class="text-zinc-200 break-words">{safe(selected?.job?.id) || "\u2014"}</div>
                </div>

                <div class="grid grid-cols-[110px_1fr] gap-3">
                  <div class="flex items-center gap-2 text-xs text-zinc-400">
                    <Icon icon={pptIcon} width="14" />
                    <span>{$t('dashboard.input')}</span>
                  </div>
                  <div class="text-zinc-200 break-words">
                    {safe(selected?.job?.input_key) || safe(selected?.current_input_path) || "\u2014"}
                  </div>
                </div>

                <div class="grid grid-cols-[110px_1fr] gap-3">
                  <div class="flex items-center gap-2 text-xs text-zinc-400">
                    <Icon icon={folderIcon} width="14" />
                    <span>{$t('dashboard.output')}</span>
                  </div>
                  <div class="text-zinc-200 break-words">
                    {safe(selected?.job?.output_key) || safe(selected?.current_output_dir) || "\u2014"}
                  </div>
                </div>

                <div class="grid grid-cols-[110px_1fr] gap-3">
                  <div class="flex items-center gap-2 text-xs text-zinc-400">
                    <Icon icon={clockIcon} width="14" />
                    <span>{$t('dashboard.runningFor')}</span>
                  </div>
                  <div class="text-zinc-200">{safe(selected?.running_for) || "\u2014"}</div>
                </div>

                <div class="grid grid-cols-[110px_1fr] gap-3">
                  <div class="flex items-center gap-2 text-xs text-zinc-400">
                    <Icon icon={clockIcon} width="14" />
                    <span>{$t('dashboard.updatedAgo')}</span>
                  </div>
                  <div class="text-zinc-200">{safe(selected?.updated_ago) || "\u2014"}</div>
                </div>
              </div>

              {#if selected?.error}
                <div
                  class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
                >
                  <Icon icon={alertIcon} width="18" />
                  <span class="break-words">{safe(selected.error)}</span>
                </div>
              {/if}
            </div>

            {#if (selected?.log?.tail || []).length}
              <div class={`${subcard} p-4`}>
                <div class="flex items-center gap-2 text-sm font-semibold text-zinc-100">
                  <Icon icon={textIcon} width="18" />
                  <span>{$t('dashboard.logTail')}</span>
                </div>

                <div
                  class="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 font-mono text-xs leading-relaxed text-zinc-200 whitespace-pre-wrap"
                >
                  {#each selected.log.tail as line (line)}
                    <div>{line}</div>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </section>
</main>
